/**
 * Pure decision helpers for the Delete Network Dialog.
 *
 * The dialog is a thin React shell around these functions. All state that
 * depends on the impact response or user input is derived here so Vitest
 * can cover every button state without spinning up a real DOM:
 *
 *   * Should the dialog render the BLOCKED state (active deployments
 *     exist) or the DELETABLE state (type-to-confirm form)?
 *   * Should the "Delete" button be enabled right now?
 *   * What human-friendly summary copy describes what will be wiped?
 *
 * The type-to-confirm predicate is reused from ``restoreDialog.js`` so
 * both destructive flows feel identical (same muscle memory: type the
 * network name, then click the destructive button). Diverging the two
 * predicates would make one flow stricter than the other for no reason.
 */
import { confirmMatchesNetworkName } from './restoreDialog';

export { confirmMatchesNetworkName };

/**
 * Three possible dialog modes the React shell switches on.
 *
 *   LOADING   — impact request in flight; render skeleton.
 *   BLOCKED   — at least one ACTIVE_RELEASE exists; render the
 *               "deactivate first" message + release list. No confirm
 *               input shown.
 *   DELETABLE — no active releases; render the typed-confirm form +
 *               the snapshot impact summary.
 *
 * Kept as plain string constants (not an enum) so they round-trip cleanly
 * in JSON-snapshot tests if we ever add them.
 */
export const DELETE_MODE_LOADING = 'LOADING';
export const DELETE_MODE_BLOCKED = 'BLOCKED';
export const DELETE_MODE_DELETABLE = 'DELETABLE';

/**
 * Single source of truth for which dialog state to render. The React
 * shell must call this before deciding whether to render the typed-
 * confirm input — otherwise a malformed impact response could leak the
 * destructive form into a state where it shouldn't appear.
 *
 * Rules:
 *   1. While the impact request is in flight (``loading``) → LOADING.
 *   2. If the impact response is missing entirely (e.g. fetch failed
 *      and the caller still mounted us) → BLOCKED. Failing closed is
 *      always safer than failing open for destructive flows.
 *   3. If ``canDelete === false`` → BLOCKED. The server is the source
 *      of truth.
 *   4. Otherwise → DELETABLE.
 */
export function resolveDeleteMode({ impact, loading } = {}) {
  if (loading) return DELETE_MODE_LOADING;
  if (!impact || typeof impact !== 'object') return DELETE_MODE_BLOCKED;
  if (impact.canDelete !== true) return DELETE_MODE_BLOCKED;
  return DELETE_MODE_DELETABLE;
}

/**
 * The dialog's submit-enabled predicate. Mirrors ``canSubmitRestore`` in
 * shape so the two flows are visually consistent (same disabled tooltip
 * triggers, same enable conditions).
 *
 * Rules:
 *   1. Mode must be DELETABLE — never let the BLOCKED state's buttons
 *      submit (defense-in-depth; the BLOCKED state shouldn't even
 *      render the input).
 *   2. ``loading`` and ``submitting`` disable the button.
 *   3. Typed token must EXACTLY match the network name (same rule as
 *      the server's ``?confirm=`` gate). No normalisation, no case
 *      folding — the server is case-sensitive too.
 */
export function canSubmitDelete({
  impact,
  networkName,
  typedConfirm,
  loading,
  submitting,
} = {}) {
  if (loading || submitting) return false;
  const mode = resolveDeleteMode({ impact, loading: false });
  if (mode !== DELETE_MODE_DELETABLE) return false;
  if (!confirmMatchesNetworkName(typedConfirm, networkName)) return false;
  return true;
}

/**
 * Defensive accessor — returns the snapshot list as an array of
 * ``{capturedAt, label}`` rows the dialog renders. Strips items missing
 * a ``capturedAt`` (the React key prop) and clamps long labels so the
 * dialog never grows beyond a sensible height.
 *
 * Hard cap = 50 rows in the dialog itself; the impact endpoint already
 * caps at ``_USER_SNAPSHOT_KEEP``, so this is just a defense layer.
 */
export function listDeletionSnapshots(impact, { hardCap = 50 } = {}) {
  if (!impact || !Array.isArray(impact.userSnapshots)) return [];
  const out = [];
  for (const row of impact.userSnapshots) {
    if (!row || typeof row !== 'object') continue;
    const at = (row.capturedAt || '').trim();
    if (!at) continue;
    out.push({
      capturedAt: at,
      label: typeof row.label === 'string' ? row.label : '',
    });
    if (out.length >= hardCap) break;
  }
  return out;
}

/**
 * Returns the active releases the dialog shows in the BLOCKED state.
 * Sorted by release version descending so the most recent deployment
 * appears first (matches creator mental model — "the latest one is the
 * scariest one").
 */
export function listBlockingReleases(impact) {
  if (!impact || !Array.isArray(impact.activeReleases)) return [];
  const rows = impact.activeReleases
    .filter((r) => r && Number.isFinite(Number(r.releaseVersion)))
    .map((r) => ({
      releaseVersion: Number(r.releaseVersion),
      releasedAt: r.releasedAt || null,
      releasedBy: r.releasedBy || null,
      pinnedPools: Array.isArray(r.pinnedPools) ? r.pinnedPools : [],
      pinnedPoolCount:
        typeof r.pinnedPoolCount === 'number'
          ? r.pinnedPoolCount
          : (Array.isArray(r.pinnedPools) ? r.pinnedPools.length : 0),
    }));
  rows.sort((a, b) => b.releaseVersion - a.releaseVersion);
  return rows;
}

/**
 * Human-friendly one-liner for the deletable state, e.g.:
 *
 *   "This will delete the network and 3 saved snapshots, 2 deactivated
 *    releases, 7 auto-checkpoints, and the golden baseline."
 *
 * The wording is intentionally heavy — destructive flows benefit from
 * "this will delete X, Y, and Z" copy that names every category, not a
 * vague "this is permanent" hand-wave.
 */
export function describeDeletionImpact(impact) {
  if (!impact) return '';
  const parts = [];
  const snaps = Number(impact.userSnapshotCount || 0);
  if (snaps > 0) {
    parts.push(`${snaps} saved snapshot${snaps === 1 ? '' : 's'}`);
  }
  const deact = Number(impact.deactivatedReleaseCount || 0);
  if (deact > 0) {
    parts.push(`${deact} deactivated release${deact === 1 ? '' : 's'}`);
  }
  const checkpoints = Number(impact.checkpointCount || 0);
  if (checkpoints > 0) {
    parts.push(
      `${checkpoints} auto-checkpoint${checkpoints === 1 ? '' : 's'}`,
    );
  }
  if (impact.baselinePresent) {
    parts.push('the golden baseline');
  }
  if (parts.length === 0) {
    return (
      'This will permanently delete the network and all of its agent ' +
      'data. This cannot be undone.'
    );
  }
  let body;
  if (parts.length === 1) {
    body = parts[0];
  } else if (parts.length === 2) {
    body = `${parts[0]} and ${parts[1]}`;
  } else {
    body = `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
  }
  return (
    `This will permanently delete the network, all of its agent data, ` +
    `${body}. This cannot be undone.`
  );
}

/**
 * Short summary used in the BLOCKED state header, e.g.:
 *
 *   "1 active deployment (3 pool members affected)"
 *   "2 active deployments"
 */
export function describeBlockingReleases(impact) {
  const releases = listBlockingReleases(impact);
  if (releases.length === 0) return '';
  const totalPools = releases.reduce(
    (sum, r) => sum + (r.pinnedPoolCount || 0),
    0,
  );
  const head =
    releases.length === 1
      ? '1 active deployment'
      : `${releases.length} active deployments`;
  if (totalPools === 0) return head;
  const tail =
    totalPools === 1
      ? '1 pool member affected'
      : `${totalPools} pool members affected`;
  return `${head} (${tail})`;
}
